import {
  ladderInvalidReason,
  pctToGate,
  persistedLadderBands,
  priceToCents,
} from './sniperArm';
import type { LadderBand, PatchSniperMarketDto } from '../types/sniper.types';

/**
 * A patch to apply to every selected market — only the fields the operator typed.
 * DERIVED from the wire DTO (not hand-duplicated) so the bulk bar can never drift
 * from the contract it writes to.
 */
export type SniperBulkPatch = Pick<
  PatchSniperMarketDto,
  | 'size'
  | 'confidenceGate'
  | 'ladderBands'
  | 'floorPrice'
  | 'varPreventiveExitOffset'
  | 'sellOnVar'
  | 'sellOnAnnulledGoal'
  | 'varRebuyEnabled'
  | 'autoNoEnabled'
  | 'autoNoIncludeExtraTime'
  | 'noMaxPrice'
  | 'noSize'
  | 'enabled'
>;

export interface ParsedBulkPatch {
  patch: SniperBulkPatch;
  /** At least one typed field is valid AND none typed is invalid. */
  canApply: boolean;
  /** Why Apply is DEAD. Present only when `canApply` is false. */
  reason?: string;
  /**
   * Something the operator should know that does NOT block Apply — e.g. a staged
   * edit that will write nothing. Kept separate so `reason` never explains a
   * button that is perfectly clickable.
   */
  note?: string;
}

/**
 * The bulk bar's ladder staging value. `null` = leave the ladder untouched,
 * which is what the bar starts at — unlike the numeric fields there is no
 * "blank" string that could stand for it.
 *
 * `touched` narrows it further, per field: the popover has to seed the floor and
 * the VAR offset from SOMETHING (there is no per-selection value to read), so
 * writing them unconditionally would stamp one market's floor — a number the
 * code's own doc says must sit below that market's pre-event price — across the
 * whole selection, and reset an offset someone deliberately tuned. Only what the
 * operator actually edited inside the popover is written.
 */
export interface BulkLadderOverlay {
  ladderBands: LadderBand[];
  floorPrice: number | null;
  varPreventiveExitOffset: number;
  touched: {
    ladderBands: boolean;
    floorPrice: boolean;
    varPreventiveExitOffset: boolean;
  };
}

/** One selected market, as the server currently holds it. */
export interface BulkSelectionMarket {
  /** Operator-facing name, so a blocker can say WHICH market is in the way. */
  marketLabel: string;
  floorPrice: number | null;
  /** EFFECTIVE values — a legacy `null` already resolved to its default. */
  sellOnVar: boolean;
  sellOnAnnulledGoal: boolean;
  autoNoEnabled: boolean;
  /** `null` is the schema default, and reachable by clearing the No $ cell. */
  noMaxPrice: number | null;
  ladderBands: LadderBand[] | null;
}

/**
 * Why this patch would be rejected for one of the selected markets — or `null`
 * when every one of them survives it.
 *
 * The server does not validate the patch: it validates
 * `validateMarketFields(mergedRow(doc, patch))` for EVERY row the patch routes
 * through, and one failure rejects the whole bulk. So a patch that is perfectly
 * well-formed on its own still 400s if a selected market's STORED config cannot
 * absorb it — a migrated row with `ladderBands: []`, or a legacy row whose
 * `sellOnVar` reads as ON with no floor. Checking only the staged overlay (or
 * only the fields the operator typed) misses all of those.
 *
 * The routing condition mirrors the service: any trigger/ladder field, enabling
 * the market, or an auto-NO change on a market whose tail ends up ON. Once
 * routed, this replays what `validateMarketFields` checks on the merged row —
 * BOTH the auto-NO cross-field rule and the ladder, since routing to half the
 * validation is the same blind spot as not routing at all.
 *
 * It deliberately does NOT replay the trigger half (half-integer line, team iff
 * `team_*`, corners full-match-only): those are set at create time, the FE never
 * bulk-edits them, and a legacy row carrying one still earns a server-labelled
 * 400 that names the market. Do not let that gap grow.
 */
export function bulkBlockedReason(
  patch: SniperBulkPatch,
  selection: BulkSelectionMarket[],
): string | null {
  const touchesLadder =
    patch.ladderBands !== undefined ||
    patch.floorPrice !== undefined ||
    patch.varPreventiveExitOffset !== undefined ||
    patch.sellOnVar !== undefined ||
    patch.sellOnAnnulledGoal !== undefined;
  const touchesAutoNo =
    patch.autoNoEnabled !== undefined || patch.noMaxPrice !== undefined;

  for (const m of selection) {
    const routesFullValidation =
      touchesLadder ||
      patch.enabled === true ||
      (touchesAutoNo && (patch.autoNoEnabled ?? m.autoNoEnabled));
    if (!routesFullValidation) continue;

    // `validateMarketFields`'s own cross-field rule, checked BEFORE the ladder:
    // a tail that ends up ON needs a ceiling, and `null` is both the schema
    // default (every migrated row) and reachable by clearing the No $ cell.
    const autoNo = patch.autoNoEnabled ?? m.autoNoEnabled;
    const ceiling =
      patch.noMaxPrice !== undefined ? patch.noMaxPrice : m.noMaxPrice;
    if (autoNo && ceiling == null) {
      return `“${m.marketLabel}” needs a No $ ceiling before its auto-NO tail can be enabled — set one here, or deselect it.`;
    }

    // The row as the server will hold it once this patch lands.
    const reason = ladderInvalidReason(
      patch.ladderBands ?? persistedLadderBands(m.ladderBands),
      patch.floorPrice ?? m.floorPrice,
      {
        varPreventiveExitOffset: patch.varPreventiveExitOffset,
        floorRequired:
          (patch.sellOnVar ?? m.sellOnVar) ||
          (patch.sellOnAnnulledGoal ?? m.sellOnAnnulledGoal),
      },
    );
    if (reason) {
      return `“${m.marketLabel}” would be left invalid — ${reason}. Fix that market on its own row, or deselect it.`;
    }
  }
  return null;
}

/** The bulk bar's raw draft — every field is "blank = leave untouched". */
export interface BulkDraft {
  size?: string;
  /** Confidence gate as an integer PERCENT (1..100). */
  gate?: string;
  noMaxPrice?: string;
  noSize?: string;
  /** Tri-state booleans: `''` untouched, `'true'`/`'false'` set the flag. */
  sellOnVar?: string;
  sellOnAnnulledGoal?: string;
  varRebuyEnabled?: string;
  autoNoEnabled?: string;
  autoNoIncludeExtraTime?: string;
  /** `null` leaves the ladder untouched; an overlay REPLACES the rungs. */
  ladder?: BulkLadderOverlay | null;
  /**
   * The markets this patch will hit, as they are stored TODAY. A patch that
   * omits `floorPrice` does not clear it: the server merges each row and
   * re-validates the WHOLE merged config, so one market can 400 the entire bulk.
   * Both directions are checked from this — the highest stored floor for the
   * `floor < bands[0].buyPrice` bound, and the markets with NO floor for the
   * "something sells, so a floor is required" bound.
   */
  selection?: BulkSelectionMarket[];
}

/**
 * Parse the bulk-bar draft strings into a patch carrying ONLY the params the
 * operator typed and that are valid. A blank field is omitted (leave untouched).
 * Validation mirrors `SniperMarketRow` AND the backend DTO: size >= 1 (server is
 * `@Min(1)` — accepting a fractional size here only earns a 400 on the whole
 * bulk), `noMaxPrice` ∈ (0,1], `noSize` >= 1, gate a percent 1..100 (→ 0..1 via
 * `pctToGate`). The five switches (`sellOnVar` / `sellOnAnnulledGoal` /
 * `varRebuyEnabled` / `autoNoEnabled` / `autoNoIncludeExtraTime`) are tri-state
 * on the wire: `''` = leave untouched, `'true'`/`'false'` = set the boolean; any
 * other string is invalid. Any typed-but-invalid field blocks Apply so a bad
 * value can't silently skip. NOTE: unlike the per-row field, bulk `noSize` blank
 * means "untouched" (the bar's universal convention), NOT "explicitly clear to
 * inherit `size`" — clearing to inherit is a per-row-only action for now.
 *
 * `ladder` is the one non-string param: `null` leaves the rungs untouched, an
 * overlay writes only the fields its `touched` flags mark (see
 * `BulkLadderOverlay`) and must pass the same `ladderInvalidReason` the row and
 * the server enforce. A floor is never DEMANDED here — each market keeps its own
 * — but the floor the server would end up holding (`mergedFloorPrice` when the
 * operator did not type one) is still range-checked against the new rungs.
 */
export function parseBulkPatch({
  size = '',
  gate = '',
  noMaxPrice = '',
  noSize = '',
  sellOnVar = '',
  sellOnAnnulledGoal = '',
  varRebuyEnabled = '',
  autoNoEnabled = '',
  autoNoIncludeExtraTime = '',
  ladder = null,
  selection = [],
}: BulkDraft = {}): ParsedBulkPatch {
  const patch: SniperBulkPatch = {};
  let invalid = false;
  let any = false;
  let reason: string | undefined;
  let note: string | undefined;

  if (size !== '') {
    const s = Number(size);
    if (Number.isFinite(s) && s >= 1) {
      patch.size = s;
      any = true;
    } else invalid = true;
  }
  // Auto-NO ceiling — a dollar price, (0,1].
  if (noMaxPrice !== '') {
    const n = Number(noMaxPrice);
    if (Number.isFinite(n) && n > 0 && n <= 1) {
      patch.noMaxPrice = n;
      any = true;
    } else invalid = true;
  }
  // Auto-NO leg size — contracts, same `>= 1` rule as `size`.
  if (noSize !== '') {
    const ns = Number(noSize);
    if (Number.isFinite(ns) && ns >= 1) {
      patch.noSize = ns;
      any = true;
    } else invalid = true;
  }
  const bools: Array<
    [
      string,
      (
        | 'sellOnVar'
        | 'sellOnAnnulledGoal'
        | 'varRebuyEnabled'
        | 'autoNoEnabled'
        | 'autoNoIncludeExtraTime'
      ),
    ]
  > = [
    [sellOnVar, 'sellOnVar'],
    [sellOnAnnulledGoal, 'sellOnAnnulledGoal'],
    [varRebuyEnabled, 'varRebuyEnabled'],
    [autoNoEnabled, 'autoNoEnabled'],
    [autoNoIncludeExtraTime, 'autoNoIncludeExtraTime'],
  ];
  for (const [raw, key] of bools) {
    if (raw !== '') {
      if (raw === 'true' || raw === 'false') {
        patch[key] = raw === 'true';
        any = true;
      } else invalid = true;
    }
  }
  if (gate !== '') {
    const g = pctToGate(Number(gate)); // 1..100 → 0..1, else null
    if (g !== null) {
      patch.confidenceGate = g;
      any = true;
    } else invalid = true;
  }
  // The market holding the HIGHEST stored floor: `floor < bands[0].buyPrice`
  // fails on that one first, and naming it is what makes the blocker actionable
  // (the operator never typed that number and cannot see it in the popover).
  const highestStoredFloor = selection
    .filter((m) => m.floorPrice != null)
    .reduce<BulkSelectionMarket | null>(
      (worst, m) => (worst == null || m.floorPrice! > worst.floorPrice! ? m : worst),
      null,
    );

  if (ladder) {
    const { touched } = ladder;
    // The floor is NEVER required here: each selected market keeps its own, and
    // demanding one would force the operator to overwrite every market's floor
    // just to add a rung. What IS checked is the floor the server will actually
    // hold after this patch — the typed one, or the highest already stored.
    const typedFloor = touched.floorPrice ? ladder.floorPrice : null;
    const effectiveFloor = typedFloor ?? highestStoredFloor?.floorPrice ?? null;
    const ladderReason = ladderInvalidReason(ladder.ladderBands, effectiveFloor, {
      varPreventiveExitOffset: touched.varPreventiveExitOffset
        ? ladder.varPreventiveExitOffset
        : null,
      floorRequired: false,
    });
    if (ladderReason !== null) {
      invalid = true;
      // A stored floor is invisible in this popover, so say whose it is and what
      // it holds — "deselect it" is a real fix the operator would not guess.
      reason =
        typedFloor == null && highestStoredFloor && ladderReason.startsWith('floor')
          ? `“${highestStoredFloor.marketLabel}” already stores a floor of ${priceToCents(highestStoredFloor.floorPrice)}, which the new rungs would break (${ladderReason}) — set a floor here or deselect that market`
          : ladderReason;
    } else {
      if (touched.ladderBands) {
        patch.ladderBands = ladder.ladderBands;
        any = true;
      }
      if (touched.floorPrice && ladder.floorPrice != null) {
        patch.floorPrice = ladder.floorPrice;
        any = true;
      } else if (touched.floorPrice) {
        // The floor was CLEARED. `PatchSniperMarketDto` cannot express "unset",
        // so this writes nothing. It is a NOTE, not a blocker: with rungs also
        // staged the Apply button is live and doing exactly what it says, and
        // only the floor half is a no-op.
        note =
          'clearing the floor here writes nothing — each market keeps its own. Type a floor, or clear the staged ladder.';
      }
      if (touched.varPreventiveExitOffset) {
        patch.varPreventiveExitOffset = ladder.varPreventiveExitOffset;
        any = true;
      }
    }
  }

  // Finally: would any SELECTED market be left invalid once the server merges
  // this patch onto it? That is what actually 400s the bulk, and it is a
  // strictly wider net than anything above — it sees stored ladders the operator
  // never opened (a migrated `[]`), legacy sell switches, and the auto-NO route.
  if (!invalid) {
    const blocked = bulkBlockedReason(patch, selection);
    if (blocked) {
      invalid = true;
      reason = blocked;
    }
  }

  return { patch, canApply: any && !invalid, reason, note };
}
